<template>
	<Teleport to="body">
		<div class="tracked-time-overlay appointments-typography" @click.self="$emit('close')">
			<form
				class="tracked-time-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="tracked-time-title"
				@submit.prevent="submit"
				@keydown.esc="$emit('close')"
			>
				<header class="tracked-time-header">
					<h3 id="tracked-time-title">{{ job.running ? 'Stop timer' : 'Change start and stop' }}</h3>
					<p>{{ job.cleaner }}, {{ job.site }}</p>
					<p>{{ formatLondonLongDay(baseDay) }}, London time</p>
				</header>

				<div class="tracked-time-fields">
					<label class="tracked-time-field">
						<span>Start</span>
						<input ref="startInput" v-model="startTime" type="time" required />
					</label>
					<label class="tracked-time-field">
						<span>Stop</span>
						<input v-model="stopTime" type="time" required />
					</label>
				</div>

				<p class="tracked-time-summary" aria-live="polite">
					<template v-if="!problem">
						<strong>Worked {{ formatDuration(seconds) }}</strong>
						<span v-if="job.plannedSeconds"> of {{ formatDuration(job.plannedSeconds) }} planned</span>
						<span v-if="stopDay !== baseDay">, stopping on {{ formatLondonLongDay(stopDay) }}</span>
					</template>
					<span v-else class="tracked-time-problem">{{ problem }}</span>
				</p>
				<p v-if="error" class="tracked-time-error" role="alert">{{ error }}</p>

				<footer class="tracked-time-actions">
					<button type="button" class="dialog-btn is-outline" @click="$emit('close')">Cancel</button>
					<button type="submit" class="dialog-btn is-primary" :disabled="Boolean(problem) || saving">
						{{ saving ? 'Saving…' : job.running ? 'Stop timer' : 'Save times' }}
					</button>
				</footer>
			</form>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import {
	formatDuration,
	formatLondonLongDay,
	formatLondonTime,
	londonDayKey,
	londonTimeToIso,
	shiftDayKey,
} from '@/utils/liveMap';

const props = defineProps({
	// { cleaner, site, day (YYYY-MM-DD), startAt, endAt, plannedSeconds, running }
	job: { type: Object, required: true },
	saving: { type: Boolean, default: false },
	error: { type: String, default: '' },
});
const emit = defineEmits(['save', 'close']);

// A job the timer never ran on starts at nine, so there is something to correct.
const DEFAULT_START = '09:00';
const DEFAULT_LENGTH_SECONDS = 3600;
// The office's clock and the server's may differ a little.
const NOW_TOLERANCE_MS = 60 * 1000;

const startInput = ref(null);
const firstStart = props.job.startAt || londonTimeToIso(props.job.day, DEFAULT_START);
const baseDay = londonDayKey(new Date(firstStart));

// A running timer stops after its planned hours, or now if that comes first.
const firstStop = (() => {
	if (props.job.endAt && !props.job.running) return props.job.endAt;
	const length = (props.job.plannedSeconds || (props.job.running ? 0 : DEFAULT_LENGTH_SECONDS)) * 1000;
	const planned = length ? Date.parse(firstStart) + length : Number.POSITIVE_INFINITY;
	return new Date(props.job.running ? Math.min(planned, Date.now()) : planned).toISOString();
})();

const startTime = ref(formatLondonTime(firstStart));
const stopTime = ref(formatLondonTime(firstStop));

// A stop earlier in the day than the start is on the next day.
const stopDay = computed(() => (stopTime.value && stopTime.value < startTime.value ? shiftDayKey(baseDay, 1) : baseDay));
const startAt = computed(() => (startTime.value ? londonTimeToIso(baseDay, startTime.value) : null));
const endAt = computed(() => (stopTime.value ? londonTimeToIso(stopDay.value, stopTime.value) : null));
const seconds = computed(() => (startAt.value && endAt.value ? (Date.parse(endAt.value) - Date.parse(startAt.value)) / 1000 : 0));

const problem = computed(() => {
	if (!startAt.value || !endAt.value) return 'Enter a start and a stop time.';
	if (seconds.value < 60) return 'Stop must be after start.';
	if (Date.parse(endAt.value) > Date.now() + NOW_TOLERANCE_MS) return 'Stop cannot be later than now.';
	return '';
});

const submit = () => {
	if (problem.value || props.saving) return;
	emit('save', { startAt: startAt.value, endAt: endAt.value });
};

onMounted(() => startInput.value?.focus());
</script>

<style scoped>
.tracked-time-overlay {
	position: fixed;
	inset: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(15, 23, 42, 0.45);
	backdrop-filter: blur(3px);
}

.tracked-time-dialog {
	display: flex;
	flex-direction: column;
	gap: 18px;
	width: min(420px, 100%);
	padding: 20px 24px;
	box-sizing: border-box;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 30px 80px rgba(15, 23, 42, 0.2);
	color: var(--text-color);
	animation: tracked-time-rise 0.2s ease-out;
}

.tracked-time-header h3 {
	margin: 0 0 4px;
	font: var(--ap-type-modal-title);
}

.tracked-time-header p {
	margin: 0;
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

.tracked-time-fields {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
}

.tracked-time-field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	color: var(--text-color-secondary);
	font: var(--ap-type-label);
}

.tracked-time-field input {
	height: 36px;
	padding: 0 10px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
	color: var(--text-color);
	font: var(--ap-type-body);
	font-variant-numeric: tabular-nums;
}

.tracked-time-field input:focus {
	outline: none;
	border-color: var(--noc-blue);
	box-shadow: 0 0 0 1px var(--noc-blue);
}

.tracked-time-summary,
.tracked-time-error {
	margin: 0;
	font: var(--ap-type-body);
	font-variant-numeric: tabular-nums;
}

.tracked-time-summary strong {
	font-weight: 600;
}

.tracked-time-problem,
.tracked-time-error {
	color: var(--noc-red);
}

.tracked-time-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
}

.dialog-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 36px;
	padding: 0 14px;
	border-radius: 8px;
	font: var(--ap-type-control);
	cursor: pointer;
}

.dialog-btn:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.dialog-btn.is-outline {
	border: 1px solid var(--border-color, #dcdcdc);
	background: var(--noc-background, #fff);
	color: var(--text-color);
}

.dialog-btn.is-outline:hover {
	background-color: color-mix(in srgb, var(--text-color-secondary, #5e6170) 4%, transparent);
}

.dialog-btn.is-primary {
	border: 1px solid var(--noc-blue);
	background: var(--noc-blue);
	color: #fff;
}

.dialog-btn.is-primary:hover:not(:disabled) {
	border-color: var(--primary-hover);
	background: var(--primary-hover);
}

@keyframes tracked-time-rise {
	from {
		transform: translateY(8px);
		opacity: 0.9;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

@media (prefers-reduced-motion: reduce) {
	.tracked-time-dialog {
		animation: none;
	}
}
</style>
